// ============================================================
// DraftEditor.tsx — (B4, batch 2 read-first) the statement DRAFT:
//   • the statement details (dates, kind, retention, signed by, payee +
//     detail, proposed tick): a read-only summary with a pencil; Save /
//     Cancel only while editing (leaving with unsaved changes asks first —
//     M16; Save waits while the retention box holds invalid text — M11). A
//     NEW statement opens straight into the editor;
//   • once prepared / approved the details are LOCKED (no pencil) and "Send
//     back for changes" (#statement-send-back — the Settlement page's lock
//     box focuses it) withdraws that, with a reason;
//   • a child's / protected party's payee is never "To the client";
//   • "Before it can be issued": the shared ActionBox, each line's button
//     DOES the fix (prepare, approve, open the court order / the exact field,
//     else the page's onFix). A refused issue lists its reasons the same way
//     (the only live box);
//   • maker → checker → Issue; Approve asks first (ConfirmDialogue) and
//     sends the notes shown; a disabled button says why in visible text.
// (Audit fix batch 1, 31 Oct)
//   • H3: Save sends the version the editor OPENED from and only the fields
//     that changed — another user's change meanwhile → 409, never overwritten;
//   • H2: the final bill differs part by part → Issue waits for "I've checked
//     the difference" + a reason (MismatchCheck), sent with the issue;
//   • M5: "Mark as prepared" waits while the Settlement page has unsaved figures;
//   • M1: the preparation was withdrawn (figures changed after it) → said why.
// (Audit B2)
//   • low b: the bill difference at issue is recorded ("I've checked the
//     difference" + reason), OK'd by an approver, and issued by someone else
//     (IssueMismatchCheck); Issue says why it waits;
//   • H3: "Record the court's decision" focuses the court steps; a corrected
//     version's court-order form starts from the previous version's order.
// ============================================================
import React, { useEffect, useId, useState } from 'react'
import {
  DESTINATION_DETAIL_LABEL, DESTINATION_LABEL, netSumLabel,
  type BlockReason, type CaseStatements, type DraftFields, type FixTarget, type StatementDraft,
} from '../../../types/settlementStatement.types'
import type { NetDestination } from '../../../types/settlement.types'
import { B_PRIMARY, B_SECONDARY, B_SECONDARY_SM, BIcon, INPUT, NOTE, money } from '../../billing/billingUi'
import { Banner, PenceField, useBadFields } from '../settlementUi'
import ActionBox, { ErrorNote, type ActionBoxAction, type ActionBoxItem } from '../../shared/ActionBox'
import DeductionsBreakdown from './DeductionsBreakdown'
import DateInput from '../../shared/DateInput'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import ConfirmDialogue from '../../shared/ConfirmDialogue'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard'
import { ukDate } from '../../../utils/civilDate'
import { EditPencil, FIGURE_GRID, Figure, Step, SummaryRow, focusById, knownChildOrPp } from './statementBits'
import { COURT_ORDER_FIRST_FIELD, COURT_STEPS_ID, CourtOrderPanel, type RecordOrder, type RecordStage } from './CourtApproval'
import { CarriedDisputeNote } from './ClientAnswer'
import SendBackDialog from './SendBackDialog'
import { IssueMismatchCheck } from './MismatchCheck'

const DESTINATIONS: NetDestination[] = ['CLIENT', 'COURT_FUNDS_OFFICE', 'CHILD_ACCOUNT', 'DEPUTY']
const SELECT = `${INPUT} pr-8`
export const SEND_BACK_ID = 'statement-send-back'

// The detail fields a blocker can point at → their element ids.
type FieldTarget = 'settlementDate' | 'damagesReceivedAt' | 'signatory' | 'netDestination' | 'retentionReason'
const FIELD_ID: Record<FieldTarget | 'kind', string> = {
  settlementDate: 'statement-field-settlement-date',
  damagesReceivedAt: 'statement-field-received',
  signatory: 'statement-field-signatory',
  netDestination: 'statement-field-destination',
  retentionReason: 'statement-field-retention-reason',
  kind: 'statement-field-kind',
}
const FIELD_TARGETS = new Set<FixTarget>(['settlementDate', 'damagesReceivedAt', 'signatory', 'netDestination', 'retentionReason', 'statement'])

export interface DraftProps {
  data: CaseStatements
  draft: StatementDraft | null
  busy: string | null
  onFix: (t: FixTarget) => void
  save: (body: DraftFields) => Promise<boolean>
  // Any edit of a detail (a save's refusal is cleared once a field changes).
  onEdit?: () => void
  prepare?: () => Promise<boolean>
  // Two-person checks off, and they can approve: both steps in one go.
  prepareAndApprove?: (review?: { notesReviewed: boolean; noteCodes: string[]; notes: { code: string; message: string }[] }) => Promise<boolean>
  askApproval?: () => Promise<boolean>
  // Who was asked just now; the server's refusal of a step, shown beside it.
  asked?: string[] | null
  actionError?: string | null
  check?: (review?: { notesReviewed: boolean; noteCodes: string[]; notes: { code: string; message: string }[] }) => Promise<boolean>
  issue?: () => void
  // (Audit B2, low b) The bill difference: record the check / an approver OKs it.
  checkMismatch?: (reason: string) => Promise<boolean>
  approveMismatch?: () => Promise<boolean>
  // (Audit fix batch 1, M5) Unsaved figures elsewhere on the page (why Prepare waits).
  figuresUnsaved?: string | null
  preview?: () => void
  recordOrder?: RecordOrder
  recordStage?: RecordStage
  sendBack?: (reason: string) => Promise<boolean>
  // (Audit fix batch 2, M7) A refused send-back — shown INSIDE its dialog.
  sendBackError?: string | null
  // The server's reasons for a refused issue (shown live, at the top).
  blockedOnIssue?: BlockReason[] | null
  onCancel?: () => void
}
const formOf = (d: StatementDraft | null, data: CaseStatements): DraftFields => ({
  settlementDate: d?.settlementDate ?? data.today,
  damagesReceivedAt: d?.damagesReceivedAt ?? null,
  kind: d?.kind ?? 'FINAL',
  retentionPence: d?.retentionPence ?? 0,
  retentionReason: d?.retentionReason ?? '',
  // A new statement's payee is set on the server by the client's capacity (a
  // child → Court Funds Office…) unless chosen here.
  netDestination: d?.netDestination,
  netDestinationDetail: d?.netDestinationDetail ?? '',
  signatoryUserId: d?.signatoryUserId ?? data.defaultSignatoryId,
  proposed: d?.proposed ?? false,
})

// (Big-audit batch 2, item 9) The approve step, said to someone who CAN
// approve: "You can approve this" — not "An approver … needs to". Everyone
// else keeps the server's wording (who has to act).
export const WAITING_FOR_COLLEAGUE = 'Waiting for a colleague to approve — you prepared it'
export const PREPARE_AND_APPROVE = 'Prepare and approve'
const listNames = (xs: string[]): string => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`)
export const approverWording = (b: Pick<BlockReason, 'code' | 'reason'>): string => {
  if (b.code === 'NOTES_NOT_REVIEWED') return 'There are notes that haven’t been reviewed (new ones may have appeared since it was approved). You can approve it again — tick “I’ve reviewed these notes” next to Approve.'
  if (b.code !== 'NOT_CHECKED') return b.reason
  if (b.reason.startsWith('An approver (')) return 'You can approve this. Check the figures, the deductions and the wording, then approve it.'
  const withdrawn = b.reason.replace(/so an approver needs to check and approve it again\.$/, 'so it needs approving again — you can approve it.')
  return withdrawn !== b.reason ? withdrawn : `${b.reason} You can approve it.`
}

// (H3) Only what differs from the details as the editor opened them.
const changedFields = (form: DraftFields, opened: DraftFields): DraftFields => {
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(form) as (keyof DraftFields)[]) if (JSON.stringify(form[k] ?? null) !== JSON.stringify(opened[k] ?? null)) out[k] = form[k]
  return out as DraftFields
}

const DraftEditor: React.FC<DraftProps> = ({ data, draft, busy, onFix, save, onEdit, prepare, prepareAndApprove, askApproval, asked, actionError, check, issue, checkMismatch, approveMismatch, preview, recordOrder, recordStage, sendBack, blockedOnIssue, onCancel, figuresUnsaved, sendBackError }) => {
  const id = useId()
  const isNew = !draft
  const [editing, setEditing] = useState(isNew)
  const [form, setForm] = useState<DraftFields>(() => formOf(draft, data))
  // (H3) The details and the row's version AS THE EDITOR OPENED — a reload
  // meanwhile (someone else's save) doesn't move them, so Save is checked
  // against what this person actually edited.
  const [opened, setOpened] = useState<{ form: DraftFields; at: string | undefined }>(() => ({ form: formOf(draft, data), at: draft?.updatedAt }))
  const [touched, setTouched] = useState(false)
  const [datesOk, setDatesOk] = useState({ settlement: true, received: true })
  const [orderOpen, setOrderOpen] = useState(false)
  const [focusReq, setFocusReq] = useState<string | null>(null)
  const [confirmApprove, setConfirmApprove] = useState(false)
  const [sendingBack, setSendingBack] = useState(false)
  const bad = useBadFields()
  const dirty = editing && touched
  const guard = useUnsavedChangesGuard(dirty)
  const set = <K extends keyof DraftFields>(k: K, v: DraftFields[K]): void => { setForm((f) => ({ ...f, [k]: v })); setTouched(true); onEdit?.() }

  const childOrPp = knownChildOrPp(draft?.capacity, data)
  const blockers = draft?.blockers ?? []
  const prepared = !!draft?.prepared
  const checked = !!draft?.checked
  const locked = !!draft && (prepared || checked)
  const advisory = draft?.advisory ?? []
  const [reviewed, setReviewed] = useState(false)
  // (Audit fix M10) The tick is for THESE notes on THESE figures: it resets
  // whenever the approval state, the note list or the figures change.
  const noteKey = advisory.map((n) => `${n.code}:${n.message}`).join('|')
  useEffect(() => { setReviewed(false) }, [prepared, checked, noteKey, draft?.approvalFingerprint])
  // Locked meanwhile (prepared elsewhere / a reload): the editors close.
  useEffect(() => { if (locked) { setEditing(false); setOrderOpen(false) } }, [locked])
  // A blocker's button asked for a field: focus it once it's on screen.
  useEffect(() => {
    if (!focusReq) return
    if (!focusById(focusReq)) focusById(FIELD_ID.kind)
    setFocusReq(null)
  }, [focusReq])

  const retentionErr = form.kind === 'INTERIM' && (form.retentionPence ?? 0) > 0 && !(form.retentionReason ?? '').trim() ? 'Say why the retention is held back.' : null
  const receivedErr = form.damagesReceivedAt && form.settlementDate && form.damagesReceivedAt < form.settlementDate ? 'The damages can’t have been received before the settlement date.' : null
  const canSave = !busy && !!form.settlementDate && datesOk.settlement && datesOk.received && !retentionErr && !receivedErr && !bad.any
  const startEdit = (focus?: string): void => {
    if (!editing) { const f = formOf(draft, data); setForm(f); setOpened({ form: f, at: draft?.updatedAt }); setTouched(false); bad.reset() }
    setEditing(true)
    if (focus) setFocusReq(focus)
  }
  const stopEdit = (): void => { setEditing(false); setTouched(false); bad.reset() }
  const onSave = async (): Promise<void> => {
    if (!canSave) return
    // A new statement sends everything; an existing one only what changed,
    // with the version it was opened from.
    const body = isNew ? form : { ...changedFields(form, opened.form), baseUpdatedAt: opened.at }
    if (!isNew && Object.keys(body).length === 1) { stopEdit(); return } // nothing changed
    if (await save(body)) { setTouched(false); if (!isNew) setEditing(false) }
  }
  const cancel = (): void => guard(() => { if (isNew) onCancel?.(); else stopEdit() })

  // A child's / protected party's money can't go to the client: never
  // offered (an old draft that still has it shows it, disabled, to change).
  // A NEW statement keeps "Set from the client's age / capacity" on offer.
  const destinations = DESTINATIONS.filter((d) => !(childOrPp === true && d === 'CLIENT'))
  const showAuto = isNew || !form.netDestination

  // ── the actions ──
  // (lib/twoPerson) The server's answer for this viewer: nothing here says "you
  // can approve" unless the server would allow it.
  const approval = draft?.approval
  const canApprove = approval ? approval.allowed : data.canCheck
  const waitingColleague = !!approval?.needsColleague && prepared && !checked
  const offerBoth = data.twoPersonChecks === false && data.canCheck && !prepared && !!prepareAndApprove
  const askLabel = approval?.askable.length ? `Ask ${listNames(approval.askable)} to approve` : null
  const approveReview = advisory.length ? { notesReviewed: true, noteCodes: advisory.map((n) => n.code), notes: advisory.map((n) => ({ code: n.code, message: n.message })) } : undefined
  const approveBlockedReason = !prepared ? 'Mark it as prepared first.' : advisory.length > 0 && !reviewed ? 'Tick “I’ve reviewed these notes” first.' : dirty ? 'Save or cancel your changes first.' : undefined
  // (Follow-ups) Preparing LOCKS the figures, so every fee-earner item must be
  // done first (the server refuses too) — only approval-stage items may remain.
  const toFinish = draft?.prepareBlockers ?? []
  const prepareBlockedReason = dirty ? 'Save or cancel your changes first.'
    : figuresUnsaved ? figuresUnsaved
    : toFinish.length && toFinish.every((b) => b.code === 'INTERIM_BILL_UNPAID') ? 'Sort out the unpaid bills above first.'
    : toFinish.length && toFinish.every((b) => b.code === 'INTERIM_BILL_UNPAID' || b.code === 'INTERIM_BILL_UNDELIVERED') ? 'Sort out the interim bills above first.'
    : toFinish.length ? `Can’t mark it as prepared yet — preparing locks the figures, so first: ${toFinish.map((b) => b.fix.label.replace(/ \(\d+\)$/, '').toLowerCase()).join('; ')}.`
      : undefined
  const bothBlockedReason = prepareBlockedReason ?? (advisory.length > 0 && !reviewed ? 'Tick “I’ve reviewed these notes” first.' : undefined)
  // (H2; Audit B2, low b) Why Issue waits for the bill check (visible next to the button).
  const issueMismatch = draft?.billMismatch ? (draft.billMismatchIssueProblem ?? (draft.billMismatchOverridden ? null : 'An approver needs to OK the difference with the final bill first.')) : null
  const openOrder = (): void => { setOrderOpen(true); setFocusReq(COURT_ORDER_FIRST_FIELD) }
  const effTarget = (t: FixTarget): FixTarget => (t === 'check' ? (prepared ? 'approve' : 'prepare') : t)
  const actionsFor = (b: BlockReason): ActionBoxAction[] => {
    const t = effTarget(b.fix.target)
    const label = b.fix.label
    if (t === 'prepare' && prepare) {
      if (offerBoth) return [
        { label, kind: 'link', onClick: () => { void prepare() }, disabled: !!prepareBlockedReason || !!busy, disabledReason: prepareBlockedReason },
        { label: PREPARE_AND_APPROVE, kind: 'primary', onClick: () => setConfirmApprove(true), disabled: !!bothBlockedReason || (!!busy && busy !== 'check' && busy !== 'prepare'), disabledReason: bothBlockedReason, busy: busy === 'prepare' || busy === 'check', busyLabel: 'Approving…' },
      ]
      return [{ label, kind: 'primary', onClick: () => { void prepare() }, disabled: !!prepareBlockedReason || (!!busy && busy !== 'prepare'), disabledReason: prepareBlockedReason, busy: busy === 'prepare', busyLabel: 'Marking…' }]
    }
    if (t === 'approve' && check) {
      if (waitingColleague) return askLabel && askApproval && !asked ? [{ label: askLabel, kind: 'primary', onClick: () => { void askApproval() }, disabled: !!busy, busy: busy === 'ask', busyLabel: 'Asking…' }] : []
      if (!canApprove) return []
      return [{ label, kind: 'primary', onClick: () => setConfirmApprove(true), disabled: !!approveBlockedReason || (!!busy && busy !== 'check'), disabledReason: approveBlockedReason, busy: busy === 'check', busyLabel: 'Approving…' }]
    }
    // (Audit B2, H3) The court's decision is recorded in the court steps.
    if (t === 'courtSteps' && childOrPp && recordStage) {
      return [{ label, kind: 'link', arrow: true, onClick: () => { const el = document.getElementById(COURT_STEPS_ID); el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }); el?.focus?.({ preventScroll: true }) } }]
    }
    if (t === 'courtOrder' && childOrPp && recordOrder) {
      if (orderOpen) return [] // the form is open below — its own button records it
      return [{ label, kind: 'link', arrow: true, onClick: openOrder, disabled: locked || !!busy, disabledReason: locked ? 'Send it back for changes first.' : undefined }]
    }
    if (FIELD_TARGETS.has(t) && draft) {
      const field = t === 'statement' ? 'settlementDate' : (t as FieldTarget)
      return [{ label, kind: 'link', arrow: true, onClick: () => startEdit(FIELD_ID[field]), disabled: locked, disabledReason: locked ? 'Send it back for changes first.' : undefined }]
    }
    return [{ label, kind: 'link', arrow: true, onClick: () => onFix(b.fix.target) }]
  }
  // (Audit fix batch 2) The checklist rows: each reason, its action on the
  // right of its own row; the maker / checker steps already done are ticked
  // ("1 of 3 done").
  // (Big-audit batch 2, item 9) The approver's "I've reviewed these notes"
  // tick sits right beside the Approve button (the notes box keeps the list).
  const reviewTick = advisory.length > 0 && (prepared || offerBoth) && canApprove && check ? (
    <label className="inline-flex items-center gap-[8px] text-[13px] font-semibold text-casevo-forest" data-review-tick="">
      <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />I’ve reviewed these notes
    </label>
  ) : null
  const itemsOf = (list: BlockReason[], withDone = false, withTick = true): ActionBoxItem[] => [
    ...(withDone && prepared ? [{ key: 'done-prepared', done: true, message: 'Marked as prepared', hint: draft?.prepared?.by ? `By ${draft.prepared.by}` : undefined }] : []),
    ...(withDone && checked ? [{ key: 'done-approved', done: true, message: 'Approved', hint: draft?.checked?.by ? `By ${draft.checked.by}` : undefined }] : []),
    ...list.map((b) => {
      const isApprove = effTarget(b.fix.target) === 'approve'
      const actions = actionsFor(b)
      const preparing = effTarget(b.fix.target) === 'prepare' && offerBoth
      const waiting = isApprove && !canApprove && !waitingColleague
      const tick = (isApprove || preparing) && withTick && actions.length > 0 ? reviewTick : null
      const askedNote = isApprove && waitingColleague && asked ? `Asked ${listNames(asked)}.` : undefined
      return { key: b.code, message: isApprove && waitingColleague ? WAITING_FOR_COLLEAGUE : isApprove && canApprove ? approverWording(b) : b.reason, hint: askedNote ?? (waiting ? 'Waiting for an approver.' : tick ?? undefined), actions }
    }),
  ]
  // A paid interim bill never marked delivered: a row to deal with, not a block.
  const paidUndelivered: ActionBoxItem[] = (draft?.warnings ?? []).filter((w) => w.code === 'INTERIM_BILL_PAID_UNDELIVERED').map((w) => ({
    key: w.code, message: w.message, hint: 'This doesn’t stop the statement being prepared or issued.',
    actions: actionsFor({ code: w.code, reason: w.message, fix: { label: 'Open Billing', target: 'billing' } }),
  }))
  // Which actions the boxes already carry (no second button for the same thing).
  const boxTargets = new Set([...blockers, ...(blockedOnIssue ?? [])].filter((b) => actionsFor(b).length > 0 || effTarget(b.fix.target) === 'approve').map((b) => effTarget(b.fix.target)))
  const hasCourtBlocker = [...blockers, ...(blockedOnIssue ?? [])].some((b) => b.fix.target === 'courtOrder')

  const signatory = data.signatories.find((o) => o.id === (draft?.signatoryUserId ?? null))
  const addressee = draft?.addressee?.name ?? (childOrPp && data.litigationFriendName ? `${data.litigationFriendName} (litigation friend)` : null)

  return (
    <div className="flex flex-col gap-4">
      {blockedOnIssue && blockedOnIssue.length > 0 && <ActionBox live tone="danger" title="The statement can’t be issued yet" items={itemsOf(blockedOnIssue, false, !blockers.some((b) => effTarget(b.fix.target) === 'approve'))} />}
      {draft?.carriedDispute && <CarriedDisputeNote d={draft.carriedDispute} />}
      {draft?.preparationWithdrawn && !prepared && (
        <Banner tone="warn" title="Figures changed after it was prepared">The preparation{draft.approvalWithdrawn ? '' : ' (and any approval)'} was withdrawn on {ukDate(String(draft.preparationWithdrawn.at).slice(0, 10))}: {draft.preparationWithdrawn.reason} Check the figures and mark it as prepared again.</Banner>
      )}
      {draft?.approvalWithdrawn && !prepared && !draft.preparationWithdrawn && (
        <Banner tone="warn" title="Figures changed after approval">The approval was withdrawn on {ukDate(String(draft.approvalWithdrawn.at).slice(0, 10))}: {draft.approvalWithdrawn.reason} Check the figures and mark it as prepared again.</Banner>
      )}
      {draft?.sentBack && !prepared && (
        <Banner tone="info" title="Sent back for changes">Sent back by {draft.sentBack.by} on {ukDate(String(draft.sentBack.at).slice(0, 10))} — ‘{draft.sentBack.reason}’</Banner>
      )}

      <section aria-label="Statement details" className="rounded-[12px] border border-casevo-border px-[14px] py-[12px] flex flex-col gap-3">
        <div className="flex items-center gap-[8px] flex-wrap">
          <h4 className="m-0 flex-1 text-[14px] font-semibold text-casevo-forest">{isNew ? 'New statement' : 'Statement details'}</h4>
          {draft && !editing && !locked && <EditPencil label="Edit the statement details" disabled={!!busy} onClick={() => startEdit()} />}
          {locked && (
            <>
              <span className="inline-flex items-center gap-[5px] text-[12.5px] text-casevo-text-soft"><BIcon name="lock" size={12} />Locked — {checked ? 'approved' : 'prepared'}</span>
              {sendBack && <button type="button" id={SEND_BACK_ID} className={B_SECONDARY_SM} disabled={!!busy} onClick={() => setSendingBack(true)}><BIcon name="undo" size={12} />Send back for changes</button>}
            </>
          )}
        </div>

        {!editing && draft ? (
          <dl className="m-0 grid gap-[12px] sm:grid-cols-2">
            <SummaryRow label="Settlement date">{draft.settlementDate ? ukDate(draft.settlementDate) : '—'}</SummaryRow>
            <SummaryRow label="Damages received">{draft.damagesReceivedAt ? ukDate(draft.damagesReceivedAt) : 'Not recorded'}</SummaryRow>
            <SummaryRow label="Statement">{draft.kind === 'INTERIM' ? 'Interim' : 'Final'}</SummaryRow>
            <SummaryRow label="Signed by">{signatory?.name ?? draft.signatoryName ?? 'Not chosen'}</SummaryRow>
            {draft.kind === 'INTERIM' && <SummaryRow label="Retention held back">{money(draft.retentionPence)}{draft.retentionReason ? ` — ${draft.retentionReason}` : ''}</SummaryRow>}
            <SummaryRow label="Where the net sum goes">{DESTINATION_LABEL[draft.netDestination]}{draft.netDestinationDetail ? <span className="block text-[12.5px] text-casevo-text-soft">{draft.netDestinationDetail}</span> : null}</SummaryRow>
            {childOrPp && <SummaryRow label="Issue as PROPOSED">{draft.proposed ? 'Yes — subject to the court’s approval' : 'No'}</SummaryRow>}
            {childOrPp && <SummaryRow label="Addressed to">{addressee ?? 'The litigation friend (not recorded yet)'}</SummaryRow>}
          </dl>
        ) : (
          <>
            <div className="grid gap-[14px] sm:grid-cols-2">
              <FormField label="Settlement date" required variant="info">
                <div id={FIELD_ID.settlementDate}>
                  <DateInput value={form.settlementDate ?? null} onChange={(v) => set('settlementDate', v ?? undefined)} ariaLabel="Settlement date" allowFuture={false}
                    onValidityChange={(ok) => setDatesOk((d) => ({ ...d, settlement: ok }))} />
                </div>
              </FormField>
              <FormField label="Date the damages were received" variant="info" helper="Shown at the top of the statement.">
                <div id={FIELD_ID.damagesReceivedAt}>
                  <DateInput value={form.damagesReceivedAt ?? null} onChange={(v) => set('damagesReceivedAt', v)} ariaLabel="Date the damages were received" allowFuture={false}
                    onValidityChange={(ok) => setDatesOk((d) => ({ ...d, received: ok }))} />
                </div>
                <FieldError message={receivedErr ?? undefined} />
              </FormField>
              <FormField label="Statement" variant="info" htmlFor={FIELD_ID.kind}>
                <select id={FIELD_ID.kind} className={SELECT} value={form.kind} onChange={(e) => set('kind', e.target.value as DraftFields['kind'])}>
                  <option value="FINAL">Final</option>
                  <option value="INTERIM">Interim</option>
                </select>
              </FormField>
              <FormField label="Signed by" required variant="info" htmlFor={FIELD_ID.signatory}>
                <select id={FIELD_ID.signatory} className={SELECT} value={form.signatoryUserId ?? ''} onChange={(e) => set('signatoryUserId', e.target.value || null)}>
                  <option value="">Choose…</option>
                  {data.signatories.map((o) => <option key={o.id} value={o.id}>{o.name}{o.jobTitle ? `, ${o.jobTitle}` : ''}{o.isFeeEarner ? ' (fee earner)' : ''}</option>)}
                </select>
              </FormField>
              {form.kind === 'INTERIM' && (
                <>
                  <PenceField label="Retention held back" pence={form.retentionPence ?? 0} onPence={(p) => set('retentionPence', p ?? 0)} onBadChange={bad.track('retention')} />
                  <FormField label="Why it’s held back" required={(form.retentionPence ?? 0) > 0} variant="info" htmlFor={FIELD_ID.retentionReason}>
                    <input id={FIELD_ID.retentionReason} className={INPUT} value={form.retentionReason ?? ''} maxLength={500} onChange={(e) => set('retentionReason', e.target.value)}
                      aria-invalid={!!retentionErr || undefined} aria-describedby={retentionErr ? `${id}-ret-err` : undefined} />
                    <span id={`${id}-ret-err`}><FieldError message={retentionErr ?? undefined} /></span>
                  </FormField>
                </>
              )}
              {childOrPp && (
                <div className="sm:col-span-2 rounded-[10px] border border-casevo-border px-[12px] py-[10px]">
                  <label className="inline-flex items-start gap-[8px] text-[13.5px] text-casevo-forest">
                    <input type="checkbox" className="mt-[3px]" checked={!!form.proposed} onChange={(e) => set('proposed', e.target.checked)} />
                    <span><b className="font-semibold">Issue as PROPOSED (subject to the court’s approval)</b>
                      <span className={`${NOTE} block`}>For the litigation friend and the approval hearing, before the court order is recorded. It has no agreement block, can’t be approved by the client and is never ready for client account. Once the order is recorded, the final statement is a new version.</span></span>
                  </label>
                </div>
              )}
              <FormField label="Where the net sum goes" variant="info" htmlFor={FIELD_ID.netDestination}>
                <select id={FIELD_ID.netDestination} className={SELECT} value={form.netDestination ?? ''} onChange={(e) => set('netDestination', (e.target.value || undefined) as NetDestination | undefined)}>
                  {showAuto && <option value="">Set from the client’s age / capacity</option>}
                  {childOrPp === true && form.netDestination === 'CLIENT' && <option value="CLIENT" disabled>To the client (not for a child or protected party)</option>}
                  {destinations.map((d) => <option key={d} value={d}>{DESTINATION_LABEL[d]}</option>)}
                </select>
              </FormField>
              {form.netDestination && <FormField label={DESTINATION_DETAIL_LABEL[form.netDestination]} variant="info" htmlFor={`${id}-detail`}>
                <input id={`${id}-detail`} className={INPUT} value={form.netDestinationDetail ?? ''} maxLength={300} onChange={(e) => set('netDestinationDetail', e.target.value)}
                  placeholder={form.netDestination === 'COURT_FUNDS_OFFICE' ? 'e.g. CFO account reference' : form.netDestination === 'DEPUTY' ? 'e.g. Jane Smith, deputy (CoP order 12345)' : ''} />
              </FormField>}
            </div>
            <div className="flex items-center gap-[10px] flex-wrap">
              <button type="button" className={B_PRIMARY} disabled={!canSave} onClick={() => { void onSave() }}>{busy === 'save' ? 'Saving…' : draft ? 'Save statement' : 'Create the draft'}</button>
              <button type="button" className={B_SECONDARY} onClick={cancel}>Cancel</button>
              {bad.any && <span className={NOTE}>Correct the amount first.</span>}
              {!isNew && touched && !bad.any && <span className={NOTE}>Unsaved changes.</span>}
            </div>
          </>
        )}
      </section>

      {draft && (
        <>
          {draft.figures && (
            <div className={FIGURE_GRID} aria-label="Statement figures">
              <Figure label="Damages" pence={draft.figures.totalDamagesPence} />
              <Figure label={draft.deductionsProposed && !draft.courtOrder ? 'Proposed deductions' : 'Deductions'} pence={draft.figures.totalDeductionsPence} />
              <Figure label="Net sum" pence={draft.figures.netToClientPence} strong sub={netSumLabel(draft.netDestination, data.representativeType)} />
            </div>
          )}
          {draft.warnings.filter((w) => w.code === 'CHILD_OVER_25K_SUMMARY' || w.code === 'COURT_OF_PROTECTION' || w.code === 'RECOVERED_ABOVE_BASE').map((w) => <Banner key={w.code} tone="warn">{w.message}</Banner>)}
          {childOrPp && recordOrder && recordStage && (
            <CourtOrderPanel draft={draft} stages={data.courtStages} busy={busy} today={data.today} open={orderOpen} setOpen={setOrderOpen}
              locked={locked} hideOpenButton={hasCourtBlocker} recordOrder={recordOrder} recordStage={recordStage}
              previousOrder={draft.supersedes ? data.statements.find((s) => s.id === draft.supersedes?.id)?.courtOrder ?? null : null} />
          )}
          {blockers.length + paidUndelivered.length > 0 && <ActionBox title="To issue this statement" ariaLabel="Before it can be issued" items={[...itemsOf(blockers, true), ...paidUndelivered]} />}
          {draft.billMismatch && checkMismatch && approveMismatch && (
            <IssueMismatchCheck key={draft.billMismatch.key} draft={draft} canApprove={data.canCheck} onlyApprover={data.onlyApprover || data.twoPersonChecks === false} busy={busy}
              record={checkMismatch} approve={approveMismatch} />
          )}
          {draft.deductionsBreakdown && <DeductionsBreakdown breakdown={draft.deductionsBreakdown} />}
          {advisory.length > 0 && (
            // (Audit fix batch 2) The new shell: the notes as facts. (Big-audit
            // batch 2, item 9) The approver's tick sits beside the Approve button.
            <ActionBox ariaLabel="Notes for the approver" tone={draft.notesReviewed ? 'done' : 'warn'} title="Notes for the approver"
              message={<>
                <span className="block">They don’t stop it being issued — the approver ticks “I’ve reviewed these notes” beside the Approve button.</span>
                <ul className="m-0 mt-[6px] pl-[18px] list-disc text-[13px] text-casevo-forest space-y-[3px]">{advisory.map((n) => <li key={n.code} data-note={n.code} style={{ whiteSpace: 'pre-line' }}>{n.message}</li>)}</ul>
                {draft.notesReviewed && <span className="block mt-[6px]">Reviewed by {draft.notesReviewed.by} on {ukDate(String(draft.notesReviewed.at).slice(0, 10))}.</span>}
              </>}
              />
          )}
          <div className={`rounded-[12px] border border-casevo-border px-[14px] py-[10px] flex items-center gap-3 flex-wrap ${blockers.length ? '' : 'bg-casevo-mint/40'}`}>
            <Step done={prepared} label="Prepared" who={draft.prepared?.by} />
            <span className="text-casevo-text-soft" aria-hidden="true">→</span>
            <Step done={checked} label="Approved" who={draft.checked?.by} />
            <span className="text-casevo-text-soft" aria-hidden="true">→</span>
            <Step done={false} label="Issued" />
            <span className="flex-1" />
            {preview && <button type="button" className={B_SECONDARY_SM} disabled={!!busy} onClick={preview}><BIcon name="eye" size={12} />Preview</button>}
            {!prepared && prepare && !boxTargets.has('prepare') && (
              <>
                <button type="button" className={B_SECONDARY} disabled={!!busy || !!prepareBlockedReason} onClick={() => { void prepare() }}>{busy === 'prepare' ? 'Marking…' : 'Mark as prepared'}</button>
                {prepareBlockedReason && <span className={NOTE}>{prepareBlockedReason}</span>}
              </>
            )}
            {prepared && !checked && check && !boxTargets.has('approve') && (waitingColleague
              ? <span className={NOTE}>{WAITING_FOR_COLLEAGUE}</span>
              : canApprove
              ? <>
                  {reviewTick}
                  <button type="button" className={B_SECONDARY} disabled={!!busy || !!approveBlockedReason} onClick={() => setConfirmApprove(true)}>{busy === 'check' ? 'Approving…' : 'Approve'}</button>
                  {approveBlockedReason && <span className={NOTE}>{approveBlockedReason}</span>}
                </>
              : <span className={NOTE}>Waiting for an approver to check it.</span>)}
            {issue && (
              <>
                <button type="button" className={B_PRIMARY} disabled={!!busy || dirty || blockers.length > 0 || !!issueMismatch}
                  onClick={() => { if (!issueMismatch) issue() }}>{busy === 'issue' ? 'Issuing…' : 'Issue statement'}</button>
                {blockers.length > 0 ? <span className={NOTE}>Fix the items above first.</span> : dirty ? <span className={NOTE}>Save or cancel your changes first.</span> : issueMismatch ? <span className={NOTE}>{issueMismatch}</span> : null}
              </>
            )}
          </div>
          {actionError && <div data-step-error=""><ErrorNote>{actionError}</ErrorNote></div>}
          {approval?.selfBasis === 'ONLY_APPROVER' && <p className={`${NOTE} m-0`}>You’re the firm’s only approver, so you can approve a statement you prepared. That is recorded in the history.</p>}
          {approval?.selfBasis === 'CHECKS_OFF' && <p className={`${NOTE} m-0`}>Two-person checks are off at this firm, so you can approve a statement you prepared. That is recorded in the history.</p>}
          {draft.checked?.selfNote && <p className={`${NOTE} m-0`} data-self-approved="">{draft.checked.selfNote}.</p>}
        </>
      )}

      {confirmApprove && check && (
        <ConfirmDialogue isOpen title="Approve this statement?" confirmLabel="Approve" tone="primary"
          message={`You confirm the figures, the deductions and the wording shown are right${advisory.length ? ', and that you have reviewed the notes for the approver' : ''}. It can then be issued.`}
          onCancel={() => setConfirmApprove(false)}
          onConfirm={() => { setConfirmApprove(false); void (offerBoth && prepareAndApprove ? prepareAndApprove(approveReview) : check(approveReview)) }} />
      )}
      {sendingBack && sendBack && <SendBackDialog busy={busy === 'sendBack'} error={sendBackError ?? null} onClose={() => setSendingBack(false)} onSend={sendBack} />}
    </div>
  )
}

export default DraftEditor
